/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useMemo, useState } from "react";
import { useInvoices } from "../../lib/react-query/useInvoices";
import LoadingIndicator from "../../components/LoadingIndicator";
import Container from "../../components/Container";
import NavigationHeader from "../../components/NavigationHeader";
import { ROUTES } from "../../constants/constants";
import ClientInvoicesTable from "../../components/client/ClientInvoicesTable";
import Paginator from "../../components/ui/Paginator";
import Dimmed from "../../components/ui/Dimmed";
import Text from "../../components/ui/Text";
import styled from "styled-components";
import Icon from "../../components/ui/Icon";
import Button from "../../components/ui/Button";
import { useNavigate } from "react-router-dom";

const StyledInvoicesWrapper = styled.div`
  display: grid;
  gap: 1rem;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 420px;
  margin: 0 auto;
  gap: 24px;
  padding-block: 48px;
  text-align: center;
`;

const ClientInvoices = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const { isLoading, isFetching, isPlaceholderData, data } = useInvoices(currentPage);

  const invoices = useMemo(() => data?.data || [], [data]);
  const totalPages = useMemo(() => data?.metadata?.pages_count || 1, [data]);

  const handlePageChange = (newPage) => {
    if (newPage <= totalPages && newPage > 0) {
      setCurrentPage(newPage);
    }
  };

  return (
    <Container>
      <NavigationHeader title="invoices_list" back={ROUTES.CLIENT_PAYMENTS} />

      {isLoading && <LoadingIndicator page />}

      {!isLoading && invoices.length === 0 && (
        <EmptyState>
          <Icon.CalendarEmpty size={Icon.SIZE.XL} color="primary" />
          <Text size="large" color="dark">
            No invoices found
          </Text>
          <Button variant="primary" title="Book now" onClick={() => navigate(ROUTES.SERVICES)} />
        </EmptyState>
      )}

      {!isLoading && invoices.length > 0 && (
        <StyledInvoicesWrapper>
          <Dimmed $active={isPlaceholderData}>
            <ClientInvoicesTable invoices={invoices} />
          </Dimmed>
          {totalPages > 1 && (
            <Paginator
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              isLoading={isFetching}
            />
          )}
        </StyledInvoicesWrapper>
      )}
    </Container>
  );
};

export default ClientInvoices;
